import { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import API_BASE_URL from "../config";
import { MapPin, ArrowLeft, Edit2, X, Check, Trash2, Camera } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import ImageWithFallback from "../components/ui/ImageWithFallback";
import GoogleMapComponent from '../components/GoogleMapComponent';
import { toast } from "sonner";

const API = API_BASE_URL.endsWith('/api') ? API_BASE_URL : `${API_BASE_URL}/api`;

const PLACEHOLDER_IMG = "https://images.unsplash.com/photo-1542721847-51ed6bd083ef?w=1200&h=500&fit=crop";
const CATEGORIES = ["temple", "trek", "lake", "waterfall", "hill_station", "wildlife", "camping", "village", "historical", "other"];
const formatCategory = (cat) => (cat ? String(cat).replaceAll("_", " ") : "");

const getHeroImage = (place) => {
  let img = place?.images?.[0];
  if (!img) return PLACEHOLDER_IMG;
  if (typeof img === "string") {
    if (img.includes("https://res.cloudinary.com")) {
      img = img.substring(img.indexOf("https://res.cloudinary.com"));
    }
    if (/^https?:\/\//i.test(img)) return img;
  }
  return `${API_BASE_URL}${img?.startsWith("/") ? "" : "/"}${img}`;
};

const isAdminUser = (user) => {
  if (!user || user === false) return false;
  return user.role === "admin" || user.is_admin === true || user.badges?.includes("admin") || user.type === "admin";
};

const getBestTimeToVisit = (category) => {
  switch (category) {
    case 'temple': return "All year round, best during festivals.";
    case 'trek': return "Pre-monsoon (May-June) or Post-monsoon (Sep-Oct).";
    case 'lake': return "Summer or early winter.";
    case 'hill_station': return "March to June.";
    default: return "Varies depending on weather.";
  }
};

export default function PlaceDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [place, setPlace] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  const [showEditPlace, setShowEditPlace] = useState(false);
  const [editName, setEditName] = useState("");
  const [editDesc, setEditDesc] = useState("");
  const [editAddress, setEditAddress] = useState("");
  const [editCategory, setEditCategory] = useState("");
  const [editSaving, setEditSaving] = useState(false);

  const [deleting, setDeleting] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        setNotFound(false);
        const res = await fetch(`${API}/places/${id}`, { credentials: "include" });
        if (!res.ok) throw new Error("Failed");
        const data = await res.json();
        setPlace(data);
      } catch {
        setPlace(null);
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id]);

  const openEditPlace = () => {
    setEditName(place?.name || "");
    setEditDesc(place?.description || "");
    setEditAddress(place?.address || place?.location?.name || "");
    setEditCategory(place?.category || "other");
    setShowEditPlace(true);
  };

  const handleSavePlace = async () => {
    if (!editName.trim()) { toast.error("Place name cannot be empty"); return; }
    setEditSaving(true);
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${API}/places/${id}`, {
        method: "PUT",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          name: editName.trim(),
          description: editDesc.trim(),
          address: editAddress.trim(),
          category: editCategory,
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "Failed to update");
      }
      const updated = await res.json();
      setPlace(updated);
      setShowEditPlace(false);
      toast.success("Place updated!");
    } catch (err) {
      toast.error(err.message || "Failed to update place");
    } finally {
      setEditSaving(false);
    }
  };

  const handleDeletePlace = async () => {
    if (!window.confirm(`Are you sure you want to delete "${place?.name}"? This cannot be undone.`)) return;
    setDeleting(true);
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${API}/places/${id}`, {
        method: "DELETE",
        credentials: "include",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "Failed to delete");
      }
      toast.success("Place deleted!");
      navigate("/places");
    } catch (err) {
      toast.error(err.message || "Failed to delete place");
    } finally {
      setDeleting(false);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Please select an image file"); return; }
    if (file.size > 10 * 1024 * 1024) { toast.error("Image must be under 10MB"); return; }

    setUploadingImage(true);
    try {
      const token = localStorage.getItem("token");
      const formData = new FormData();
      formData.append("file", file);

      const uploadRes = await fetch(`${API}/upload`, {
        method: "POST",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: formData,
      });

      if (!uploadRes.ok) throw new Error("Failed to upload image");
      const uploadData = await uploadRes.json();
      const imageUrl = uploadData.url || `${API}/files/${uploadData.path}`;

      const updateRes = await fetch(`${API}/places/${id}`, {
        method: "PUT",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ images: [imageUrl] }),
      });

      if (!updateRes.ok) throw new Error("Failed to update place image");
      const updated = await updateRes.json();
      setPlace(updated);
      toast.success("Image updated!");
    } catch (err) {
      toast.error(err.message || "Failed to upload image");
    } finally {
      setUploadingImage(false);
      e.target.value = "";
    }
  };

  if (loading) {
    return <div className="max-w-7xl mx-auto p-6"><p className="text-muted-foreground">Loading place... ⏳</p></div>;
  }

  if (notFound || !place) {
    return (
      <div className="max-w-7xl mx-auto p-6 text-center">
        <p className="text-muted-foreground">Place not found ❌</p>
        <Link to="/places" className="text-foreground font-semibold mt-3 inline-block hover:underline">Back to places</Link>
      </div>
    );
  }

  const heroImg = getHeroImage(place);
  const locationText = place.address?.trim() || place.location_name?.trim?.() || place.location?.name?.trim?.() || "Uttarakhand";
  const hasCoords = place.location?.lat != null && place.location?.lng != null;
  const admin = isAdminUser(user);
  const isOwner = user && user !== false && (user.id === place.created_by || user._id === place.created_by);
  const canEdit = admin || isOwner;

  return (
    <div className="max-w-7xl mx-auto p-6">
      {/* HERO */}
      <div className="relative overflow-hidden rounded-2xl shadow-sm border border-border">
        <div className="relative h-56 sm:h-72 md:h-80">
          <div className="absolute inset-0">
            <ImageWithFallback src={heroImg} alt={place.name} className="h-full w-full" fallbackSrc="/fallback.jpg" loading="eager" />
          </div>
          <div className="absolute inset-0 bg-gradient-to-t from-background/90 via-background/40 to-transparent" />
          <div className="absolute inset-x-0 bottom-0 p-5 sm:p-7">
            <Link to="/places" className="inline-flex items-center gap-2 text-white/80 hover:text-white text-sm">
              <ArrowLeft className="h-4 w-4" /> Back to places
            </Link>
            <div className="mt-3 flex flex-col gap-2">
              <div>
                <span className="inline-flex items-center rounded-full bg-[#10b981]/15 px-2.5 py-1 text-xs font-semibold text-[#10b981] border border-[#10b981]/20 backdrop-blur-sm">
                  {formatCategory(place.category || "other")}
                </span>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-white">{place.name}</h1>
                {canEdit && (
                  <div className="flex items-center gap-1.5">
                    <button onClick={openEditPlace} className="p-1.5 rounded-lg bg-white/20 hover:bg-white/30 text-white transition-colors" title="Edit place">
                      <Edit2 className="w-4 h-4" strokeWidth={1.5} />
                    </button>
                    <label className="cursor-pointer p-1.5 rounded-lg bg-white/20 hover:bg-white/30 text-white transition-colors" title="Change image">
                      {uploadingImage
                        ? <span className="text-xs px-1">⏳</span>
                        : <Camera className="w-4 h-4" strokeWidth={1.5} />
                      }
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={handleImageUpload}
                        disabled={uploadingImage}
                      />
                    </label>
                    {admin && (
                      <button onClick={handleDeletePlace} disabled={deleting} className="p-1.5 rounded-lg bg-red-500/70 hover:bg-red-500 text-white transition-colors disabled:opacity-60" title="Delete place">
                        <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                      </button>
                    )}
                  </div>
                )}
              </div>
              <div className="flex items-center gap-2 text-white/80">
                <MapPin className="h-4 w-4" />
                <span className="text-sm">{locationText}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* DETAILS */}
      <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Left column */}
        <div className="lg:col-span-2 space-y-4">
          {/* About */}
          <div className="bg-white rounded-2xl border border-card-border p-5 sm:p-6 shadow-sm">
            <div className="flex items-center justify-between mb-2">
              <h2 className="text-lg font-semibold text-foreground">About</h2>
              {canEdit && (
                <button onClick={openEditPlace} className="p-1.5 rounded-lg hover:bg-[#10b981]/10 text-muted-foreground hover:text-[#10b981] transition-colors" title="Edit description">
                  <Edit2 className="w-4 h-4" strokeWidth={1.5} />
                </button>
              )}
            </div>
            {place.description?.trim()
              ? <p className="text-muted-foreground leading-relaxed whitespace-pre-line">{place.description}</p>
              : <p className="text-muted-foreground">No description available.</p>
            }
          </div>



          {/* Google Maps */}
          {hasCoords && (
            <div className="bg-white rounded-2xl border border-card-border overflow-hidden shadow-sm">
              <div className="h-72">
                <GoogleMapComponent
                  initialCenter={{ lat: place.location.lat, lng: place.location.lng }}
                  height="100%"
                />
              </div>
              <div className="p-3 border-t border-border">
                <a
                  href={`https://www.google.com/maps?q=${place.location.lat},${place.location.lng}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-sm text-[#10b981] hover:text-[#059669] font-medium transition-colors"
                >
                  <MapPin className="w-4 h-4" />
                  Open in Google Maps
                </a>
              </div>
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-4">
          <div className="bg-white rounded-2xl border border-card-border p-5 sm:p-6 shadow-sm">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-lg font-semibold text-foreground">Details</h2>
              {canEdit && (
                <div className="flex items-center gap-1">
                  <button onClick={openEditPlace} className="p-1.5 rounded-lg text-muted-foreground hover:text-primary hover:bg-primary/10 transition-colors" title="Edit place">
                    <Edit2 className="w-4 h-4" strokeWidth={1.5} />
                  </button>
                  {admin && (
                    <button onClick={handleDeletePlace} disabled={deleting} className="p-1.5 rounded-lg text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors disabled:opacity-60" title="Delete place">
                      <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                    </button>
                  )}
                </div>
              )}
            </div>
            <div className="space-y-2 text-sm">
              <div className="flex items-start justify-between gap-3">
                <span className="text-muted-foreground">Location</span>
                <span className="text-foreground text-right">{locationText}</span>
              </div>
              <div className="flex items-start justify-between gap-3">
                <span className="text-muted-foreground">Category</span>
                <span className="text-foreground text-right">{formatCategory(place.category || "other")}</span>
              </div>
              <div className="flex items-start justify-between gap-3">
                <span className="text-muted-foreground">Best time</span>
                <span className="text-foreground text-right">{getBestTimeToVisit(place.category)}</span>
              </div>
              {hasCoords && (
                <div className="flex items-start justify-between gap-3">
                  <span className="text-muted-foreground">Coordinates</span>
                  <span className="text-foreground text-right">
                    {Number(place.location.lat).toFixed(4)}, {Number(place.location.lng).toFixed(4)}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Edit Place Modal */}
      {showEditPlace && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="bg-white border border-card-border rounded-2xl shadow-xl w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-lg font-semibold text-foreground">Edit Place</h2>
              <button onClick={() => setShowEditPlace(false)} className="p-1.5 rounded-lg text-muted-foreground hover:bg-gray-100 transition-colors">
                <X className="w-4 h-4" strokeWidth={1.5} />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">Place Name</label>
                <input type="text" value={editName} onChange={(e) => setEditName(e.target.value)}
                  className="w-full border border-card-border bg-white rounded-xl px-3.5 py-2.5 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary transition-colors"
                  placeholder="Place name" />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">Description</label>
                <textarea value={editDesc} onChange={(e) => setEditDesc(e.target.value)} rows={3}
                  className="w-full border border-card-border bg-white rounded-xl px-3.5 py-2.5 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary transition-colors resize-none"
                  placeholder="Description" />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">Address</label>
                <input type="text" value={editAddress} onChange={(e) => setEditAddress(e.target.value)}
                  className="w-full border border-card-border bg-white rounded-xl px-3.5 py-2.5 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary transition-colors"
                  placeholder="Address" />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">Category</label>
                <select value={editCategory} onChange={(e) => setEditCategory(e.target.value)}
                  className="w-full border border-card-border bg-white rounded-xl px-3.5 py-2.5 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary transition-colors">
                  {CATEGORIES.map((c) => <option key={c} value={c}>{formatCategory(c)}</option>)}
                </select>
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <button onClick={() => setShowEditPlace(false)} className="flex-1 border border-card-border text-text-secondary-dark rounded-xl py-2.5 text-sm font-medium hover:bg-gray-50 transition-colors">
                Cancel
              </button>
              <button onClick={handleSavePlace} disabled={editSaving}
                className="flex-1 bg-[#10b981] text-white rounded-xl py-2.5 text-sm font-medium hover:bg-[#059669] transition-colors disabled:opacity-60 flex items-center justify-center gap-2">
                {editSaving ? "Saving..." : <><Check className="w-4 h-4" strokeWidth={1.5} /> Save Changes</>}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
